import React, { useEffect } from "react";
import { Lightbulb, Sparkles, Image as ImageIcon, Paperclip, Send, CheckCircle2 } from "lucide-react";
import { useCockpit } from "./CockpitLayout";
import { Card, BtnPrimary, Badge } from "./ui";
import { Spinner } from "@/components/common";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import useCockpitRequests from "./useCockpitRequests";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS_TONE = {
  eingegangen: "blue", neu: "slate", in_pruefung: "amber", umgewandelt: "green", erledigt: "green", abgelehnt: "red",
};
const PRIO_TONE = { hoch: "red", mittel: "amber", niedrig: "slate" };

const fileUrl = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);
const isImg = (a) => (a.content_type || "").startsWith("image/") || /\.(png|jpe?g|webp)$/i.test(a.url || "");

export default function CRequests() {
  const { accent, clientId, openAi } = useCockpit();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { data, loadError, load } = useCockpitRequests({ lang, clientId,
    userId: user?.id, accountClientId: user?.client_id });
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (loadError) return (
    <div role="alert" data-testid="crequests-load-error" className="p-5">
      <p>{t("Wünsche konnten nicht geladen werden.")}</p>
      <button onClick={load} data-testid="crequests-load-retry" className="mt-3 min-h-[44px] underline">{t("Erneut versuchen")}</button>
    </div>
  );
  if (!data) return <Spinner />;

  const reqs = data.requests || [];
  const generationEnabled = data.generationEnabled;

  return (
    <div className="fade-in space-y-5" data-testid="cockpit-requests">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight">{t("Meine Wünsche")}</h1>
          <p className="text-sm text-gray-400 mt-1.5 max-w-2xl">
            {generationEnabled
              ? t("Teilen Sie KEWAG einen Wunsch mit — per Text, Foto oder Screenshot. Der KI-Assistent fasst ihn übersichtlich zusammen, damit KEWAG ihn direkt umsetzen kann.")
              : t("Teilen Sie KEWAG einen Wunsch mit — per Text, Foto oder Screenshot. KEWAG prüft ihn persönlich und setzt ihn um.")}
          </p>
        </div>
        <BtnPrimary accent={accent} onClick={openAi} data-testid="requests-new-btn">
          <Sparkles className="w-4 h-4" /> {t("Neuen Wunsch mitteilen")}
        </BtnPrimary>
      </div>

      {reqs.length === 0 ? (
        <Card className="p-8 text-center" data-testid="requests-empty">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${accent}1A` }}>
            <Lightbulb className="w-6 h-6" style={{ color: accent }} />
          </div>
          <h3 className="font-bold text-lg">{t("Noch keine Wünsche vorhanden")}</h3>
          <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
            {t("Sobald Sie einen Wunsch mitteilen, erscheint er hier mit dem aktuellen Status — von «Neu» bis «Umgesetzt».")}
          </p>
          <BtnPrimary accent={accent} onClick={openAi} data-testid="requests-empty-btn" className="mt-4 mx-auto">
            <Send className="w-4 h-4" /> {t("Ersten Wunsch mitteilen")}
          </BtnPrimary>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {reqs.map((r) => {
            const images = (r.attachments || []).filter(isImg);
            const files = (r.attachments || []).filter((a) => !isImg(a));
            return (
              <Card key={r.id} className="p-5 flex flex-col" data-testid={`request-card-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">{r.card?.subject || t("Wunsch")}</span>
                      <Badge tone={STATUS_TONE[r.status] || "slate"} accent={accent}>{r.status_label}</Badge>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                      {r.request_no} · {r.card?.category_label}
                      {r.card?.priority && <> · {t("Priorität")}: {r.card.priority_label}</>}
                    </div>
                  </div>
                  {r.ai_analyzed && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: `${accent}1F`, color: accent }}>
                      <Sparkles className="w-3 h-3" /> {t("KI")}
                    </span>
                  )}
                </div>

                {r.card?.summary && <p className="text-sm text-gray-300 mt-3">{r.card.summary}</p>}
                {r.card?.image_findings && (
                  <p className="text-xs text-gray-400 mt-2 flex items-start gap-1.5">
                    <ImageIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accent }} /> {r.card.image_findings}
                  </p>
                )}
                {r.card?.next_step && (
                  <p className="text-xs mt-2" style={{ color: accent }}>
                    <span className="font-semibold">{t("Nächster Schritt")}:</span> {r.card.next_step}
                  </p>
                )}

                {images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {images.map((a, i) => (
                      <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer" data-testid={`request-img-${r.id}-${i}`}
                        className="w-16 h-16 rounded-lg overflow-hidden border border-white/10 bg-white/5 shrink-0">
                        <img src={fileUrl(a.url)} alt={a.label || ""} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {files.map((a, i) => (
                      <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-gray-300 hover:bg-white/10">
                        <Paperclip className="w-3 h-3" /> <span className="max-w-[160px] truncate">{a.label || t("Anhang")}</span>
                      </a>
                    ))}
                  </div>
                )}

                <div className="flex-1" />
                <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] text-gray-500">
                  <span>{r.created_at ? new Date(r.created_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""}</span>
                  {r.status === "umgewandelt" && (
                    <span className="inline-flex items-center gap-1" style={{ color: accent }}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> {t("In Umsetzung bei KEWAG")}
                    </span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
